import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg style={{ width: size, height: size }} viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

function AppleIcon({ size = 20 }: { size?: number }) {
  return (
    <svg style={{ width: size, height: size }} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 12.04c-.03-2.9 2.37-4.29 2.48-4.36-1.35-1.98-3.46-2.25-4.21-2.28-1.79-.18-3.5 1.05-4.41 1.05-.91 0-2.31-1.03-3.8-1-1.96.03-3.77 1.14-4.78 2.9-2.04 3.53-.52 8.76 1.46 11.62.97 1.4 2.12 2.97 3.63 2.91 1.46-.06 2.01-.94 3.77-.94 1.76 0 2.26.94 3.8.91 1.57-.03 2.56-1.42 3.52-2.83 1.11-1.62 1.57-3.19 1.59-3.27-.03-.02-3.05-1.17-3.08-4.64zM14.19 3.66C15 2.68 15.54 1.33 15.39 0c-1.15.05-2.54.77-3.37 1.74-.75.86-1.4 2.24-1.23 3.55 1.28.1 2.59-.65 3.4-1.63z"/>
    </svg>
  )
}

export function LoginPage() {
  const { user, loading, error, signInWithGoogle, signInWithGoogleRedirect, signInWithApple } = useAuth()
  const location = useLocation()

  useEffect(() => {
    if (error) {
      console.error('Login error:', error)
    }
  }, [error])

  if (user) {
    // 認証切れでログインに飛ばされた場合は元のページへ戻す
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />
  }

  return (
    <div className="flex-1 w-full overflow-y-auto flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
      <div className="w-full px-6" style={{ maxWidth: 420 }}>
        <div className="card w-full">
          {/* ヘッダー */}
          <div className="text-center" style={{ marginBottom: 'var(--space-8)' }}>
            <span className="brand-logo text-2xl">BluePaper</span>
            <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-2)' }}>
              議事録作成・管理プラットフォーム
            </p>
          </div>

          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 className="text-lg font-semibold text-primary" style={{ marginBottom: 'var(--space-1)' }}>
              アカウントにログイン
            </h2>
            <p className="text-sm text-secondary">
              Apple または Google アカウントでサインインしてください
            </p>
          </div>

          {error && (
            <div className="alert danger" style={{ marginBottom: 'var(--space-5)' }}>
              <svg className="alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <div className="alert-body">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={signInWithGoogle}
              disabled={loading}
              className="btn btn-outline btn-lg w-full"
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  <GoogleIcon />
                  Googleでサインイン
                </>
              )}
            </button>

            <button
              onClick={signInWithApple}
              disabled={loading}
              className="btn btn-lg w-full"
              style={{ background: '#000', color: '#fff', border: '1px solid #000' }}
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  <AppleIcon />
                  Appleでサインイン
                </>
              )}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full" style={{ borderTop: '1px solid var(--color-border-light)' }}></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 text-tertiary" style={{ background: 'var(--color-base)' }}>リダイレクト方式</span>
              </div>
            </div>

            <button
              onClick={signInWithGoogleRedirect}
              disabled={loading}
              className="btn btn-ghost btn-lg w-full"
            >
              {loading ? (
                <span className="spinner sm" />
              ) : (
                <>
                  <GoogleIcon size={16} />
                  Google
                </>
              )}
            </button>
          </div>

          <div className="pt-4 mt-6" style={{ borderTop: '1px solid var(--color-border-light)' }}>
            <p className="text-xs text-tertiary text-center">
              ポップアップがブロックされる場合は、リダイレクト方式をご利用ください
            </p>
          </div>
        </div>

        {/* フッター */}
        <div className="mt-8 text-center">
          <p className="text-xs text-tertiary">
            <a href="/privacy" className="text-tertiary" style={{ textDecoration: 'underline' }}>プライバシーポリシー</a>
          </p>
          <p className="text-xs text-tertiary" style={{ marginTop: 4 }}>
            © 2026 BluePaper. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
